import { ChangeEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  BreadcrumbHeading,
  Chip,
  InfoCard,
  InfoCardDetail,
  MenuIcon,
  MultiSelect,
  Plus,
  PriceBandIcon,
  SearchField,
  SoftButton,
  Spinner,
  Typography,
} from '@weareopenr/component-library';
import { Routes } from 'src/routes';
import { pluralize } from 'src/helpers/pluralize/pluralize';
import { useDebounce } from 'src/hooks/useDebounce/useDebounce';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { useRanges } from 'src/hooks/useRanges/useRanges';
import { useMenuSets } from 'src/hooks/useMenuSets';
import { useTimetableRangesEnabled } from 'src/hooks/useTimetableRangesEnabled';
import { findImageUrl } from 'src/hooks/useMenu/formatMenu/formatMenu';
import { IMenuSet } from 'src/api/getMenuSets';
import SkeletonContainer from 'src/components/SkeletonContainer';
import styles from './MenuSets.module.scss';

const MENU_SETS_PAGE_SIZE = 50;

export const MenuSets = () => {
  const { trackEvent } = useUserTracking();
  const [searchQuery, setSearchQuery] = useState<string>();
  const [selectedRangeIds, setSelectedRangeIds] = useState<string[]>([]);
  const debouncedSearchQuery = useDebounce(searchQuery);
  const rangeIds = selectedRangeIds.length > 0 ? selectedRangeIds : undefined;

  const { data: ranges } = useRanges();
  const rangeFilterOptions = useMemo(
    () => ranges?.map((range, index) => ({ id: range.range_id, label: range.name, display_order: index })) ?? [],
    [ranges],
  );
  const { isEnabled: rangesEnabled } = useTimetableRangesEnabled();

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useMenuSets(
    MENU_SETS_PAGE_SIZE,
    debouncedSearchQuery,
    rangeIds,
  );

  const { ref } = useInView({
    onChange: (inView) => {
      if (inView && hasNextPage) fetchNextPage();
    },
  });

  useEffect(() => {
    document.title = 'Menu Sets';
    trackEvent('page_viewed', { page: 'menu_sets' });
  }, [trackEvent]);

  const menuSets = data?.pages.flatMap((page) => page.data) ?? [];
  const totalCount = data?.pages[0]?.total_count ?? 0;
  const hasActiveFilters = !!debouncedSearchQuery || !!rangeIds;
  const isEmptyOverall = !isLoading && totalCount === 0 && !hasActiveFilters;

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const getCardTitleText = (menuSet: IMenuSet): string =>
    menuSet.consumer_name ? `${menuSet.name} (${menuSet.consumer_name})` : menuSet.name;

  const getCardTitle = (menuSet: IMenuSet): ReactNode => (
    <>
      <span className={styles['menu-set-name']}>{menuSet.name}</span>
      {menuSet.consumer_name && <span className={styles['menu-set-consumer-name']}> ({menuSet.consumer_name})</span>}
    </>
  );

  const getCardImage = (menuSet: IMenuSet): ReactNode => {
    const imageUrl = findImageUrl(menuSet.assets);
    if (imageUrl) return <img src={imageUrl} alt={menuSet.name} />;

    return (
      <div className={styles['image-placeholder']}>
        <MenuIcon size="xlarge" />
      </div>
    );
  };

  const getCardDetails = (menuSet: IMenuSet): InfoCardDetail[] => [
    { text: 'Menus used', detail: String(menuSet.menus.length) },
    { text: 'Total menu sections', detail: String(menuSet.sections.length) },
  ];

  const getCardFootnote = (menuSet: IMenuSet) => {
    if (!rangesEnabled) return null;
    if (menuSet.ranges.length === 0) return <Typography size="xsmall">No Ranges</Typography>;

    return menuSet.ranges.map((range) => (
      <Chip key={range.range_id} label={range.name} icon="priceBand" backgroundColour="grape" />
    ));
  };

  const getCountText = (): string => {
    if (isLoading) return 'Loading...';
    if (menuSets.length > 0) return `${totalCount} ${pluralize('menu set', totalCount)}`;
    if (hasActiveFilters) return 'No results for your search...';
    return 'Build your first Menu Set...';
  };

  return (
    <div className={styles['page-container']}>
      <div className={styles['page-header']}>
        <BreadcrumbHeading breadcrumbs={[{ label: 'Menu Sets', path: Routes.MENU_SETS }]} />
        <div className={styles['filters']}>
          <SearchField placeholder="Search for a Menu Set" onChange={handleSearchChange} isDisabled={isEmptyOverall} />
          {rangesEnabled && (
            <MultiSelect
              Icon={PriceBandIcon}
              label="Range"
              options={rangeFilterOptions}
              selected={selectedRangeIds}
              onChange={setSelectedRangeIds}
              disabled={isEmptyOverall}
            />
          )}
        </div>
        <Typography>{getCountText()}</Typography>
      </div>
      <SoftButton colour="blueberry" icon={Plus} size="full-width" onClick={() => {}}>
        Create Menu Set
      </SoftButton>
      {(isLoading || menuSets.length > 0) && (
        <div className={styles['list']}>
          {isLoading && (
            <div className={styles['skeleton-wrapper']}>
              <SkeletonContainer hiddenTitle />
            </div>
          )}
          {!isLoading &&
            menuSets.map((menuSet) => (
              <div key={menuSet.menu_set_id}>
                <InfoCard
                  label="Menu Set"
                  labelColour="grape"
                  title={getCardTitle(menuSet)}
                  titleTooltip={getCardTitleText(menuSet)}
                  image={getCardImage(menuSet)}
                  size="medium"
                  border="solid"
                  details={getCardDetails(menuSet)}
                  footnote={getCardFootnote(menuSet)}
                  onClick={() => {}}
                />
              </div>
            ))}
          {isFetchingNextPage && (
            <div className={styles['spinner-container']}>
              <Spinner size="medium" colour="rainbow" />
            </div>
          )}
          {hasNextPage && !isFetchingNextPage && <div ref={ref} />}
        </div>
      )}
    </div>
  );
};

export default MenuSets;
